import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/providers/auth-provider";
import { useState } from "react";
import { toast } from "@/hooks/use-toast";
import { UserRoundPen } from "lucide-react";
import { useTranslation } from "@/providers/language-provider";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { ButtonSubmit } from "@/components/blocks/button-submit";
import { useEffect } from "react";

export function DialogUpdateUser() {
  const { t } = useTranslation();
  const { user, changeName } = useAuth();
  // const [name, setName] = useState(authUser?.name ?? "");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 폼 스키마를 동적으로 생성
  const formSchema = z.object({
    name: z
      .string()
      .min(4, { message: "Name must be at least 4 characters long." })
      .refine((value) => value !== user?.name, {
        message: "New name must be different from current name.",
      }),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: user?.name ?? "",
    },
    mode: "onChange", // 실시간 유효성 검사
  });

  useEffect(() => {
    if (user) {
      form.reset({
        name: user.name ?? "",
      });
    }
  }, [user, form]);

  async function onSubmit(data: z.infer<typeof formSchema>) {
    setIsSubmitting(true);
    await handleChangeName(data.name);
    setIsSubmitting(false);
  }

  const handleChangeName = async (newName: string) => {
    try {
      await changeName({ newName });
      toast({
        title: "Change Name Successful:",
        description: "Change name is success.",
      });
    } catch (error) {
      console.error("Change name failed:", error);
      toast({
        title: "Change Name Failed:",
        description: `${error}`,
      });
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon">
          <UserRoundPen className="w-4 h-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t("ChangeUserInfo")}</DialogTitle>
          <DialogDescription></DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="w-full space-y-2"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required>Name</FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      placeholder="Enter your name."
                      autoComplete="username"
                      required
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">{t("Cancel")}</Button>
              </DialogClose>
              <ButtonSubmit
                type="submit"
                isSubmitting={isSubmitting}
                disabled={!form.formState.isValid}
              >
                {t("Save")}
              </ButtonSubmit>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
